/**
 * Terms of Service — wavedsp.ai/terms
 *
 * Public, unauthenticated. Required for Meta App Review (Instagram
 * Content Publishing) and TikTok Content Posting API approval.
 *
 * Plain-English voice. If you change platform fees or payout terms,
 * update this page AND notify users in-app at least 14 days ahead.
 */
import React, { useEffect } from 'react';
import LegalLayout from '../../components/legal/LegalLayout';

export default function TermsOfServicePage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Terms of Service · WAVE DSP';
    return () => { document.title = prev; };
  }, []);

  return (
    <LegalLayout title="Terms of Service" lastUpdated="June 24, 2026">
      <p>
        These Terms govern your use of WAVE DSP. By creating an account or using the
        platform, you agree to be bound by them. If you don&rsquo;t agree, please
        don&rsquo;t use WAVE.
      </p>

      <h2>1. Who can use WAVE</h2>
      <ul>
        <li>You must be <strong>at least 18 years old</strong>.</li>
        <li>You must have the legal authority to bind yourself or the entity you represent.</li>
        <li>You must not be located in a country subject to U.S. embargo or appear on any prohibited-party list.</li>
        <li>If you&rsquo;re a creator, your social accounts must comply with the platforms&rsquo; own terms (e.g., Instagram requires a Business or Creator account to publish via API).</li>
      </ul>

      <h2>2. Account responsibilities</h2>
      <ul>
        <li>You&rsquo;re responsible for all activity under your account &mdash; protect your password.</li>
        <li>One person, one account. Don&rsquo;t share login credentials.</li>
        <li>Tell us within 24 hours if you suspect unauthorized access at <a href="mailto:security@wavedsp.ai">security@wavedsp.ai</a>.</li>
      </ul>

      <h2>3. How waves work</h2>
      <p>
        A &ldquo;wave&rdquo; is a coordinated burst of sponsored social-media content
        deployed across a curated cohort of creators. Each wave has three roles:
      </p>
      <ul>
        <li><strong>Brand</strong> &mdash; defines goals, budget, KPI weights, SKU specs, and approves drafts.</li>
        <li><strong>Creators</strong> &mdash; accept the brief, produce content, get approval, post.</li>
        <li><strong>WAVE</strong> &mdash; matches, schedules, measures, and handles money.</li>
      </ul>

      <h2>4. Brand obligations</h2>
      <ul>
        <li>You must own or have rights to the products, brand marks, and SKUs in your brief.</li>
        <li>You must fund waves up-front via escrow.</li>
        <li>You must not require creators to make false, deceptive, or non-compliant claims (e.g., illegal health, financial, or product claims).</li>
        <li>You must <strong>respond to draft approvals within the SLA window (24 hours)</strong>. Unanswered drafts auto-revert per the wave&rsquo;s contract.</li>
      </ul>

      <h2>5. Creator obligations</h2>
      <ul>
        <li>All sponsored posts <strong>must include #ad</strong> or an equivalent FTC-compliant disclosure.</li>
        <li>Content must be original. No stolen footage, no copyrighted music you don&rsquo;t have rights to.</li>
        <li>You retain ownership of your content; you grant WAVE and the brand a non-exclusive licence to use, display, and re-share the wave content for the duration set in the brief.</li>
        <li>You must deliver the post type, platform, and timing committed to in the brief. Late or missed deliverables may result in clawbacks per the wave&rsquo;s contract.</li>
      </ul>

      <h2>6. Payments, escrow, and the WAVE fee</h2>
      <ul>
        <li><strong>15% transparent platform fee</strong> on every spend, shown up-front before brands fund.</li>
        <li>Brands pay into escrow when a wave is funded. Funds are released to creators after delivery is confirmed and the SLA window closes.</li>
        <li>Creator payouts are processed by <strong>Stripe Connect</strong>; transfer timing depends on Stripe&rsquo;s schedule (typically 2&ndash;7 business days).</li>
        <li>U.S.-based creators receive <strong>1099-NEC</strong> tax forms when annual earnings exceed the IRS threshold.</li>
        <li>Refunds: if a wave is cancelled before any creator has begun work, the brand is refunded in full minus payment processor fees. Once a creator has accepted, the brand pays the agreed amount unless the creator fails to deliver.</li>
      </ul>

      <h2>7. How content gets published (notify-and-post)</h2>
      <p>
        WAVE does <strong>not</strong> publish content to Instagram, TikTok, or
        YouTube on a creator&rsquo;s behalf. Our publishing model is
        <em>notify-and-post</em>:
      </p>
      <ul>
        <li>The brand approves a draft inside WAVE.</li>
        <li>The creator is notified and publishes the content <strong>natively</strong>
          using the platform&rsquo;s own app (Instagram, TikTok, or YouTube).</li>
        <li>The creator (or the brand on the creator&rsquo;s behalf) submits the
          public post URL back to WAVE so we can attach it to the wave record
          and verify completion.</li>
      </ul>
      <p>
        When a creator connects their Instagram, TikTok, or YouTube account, the
        OAuth permissions WAVE requests are strictly <strong>read-only</strong>
        &mdash; profile info, follower stats, and (for enrolled posts) public
        insights. The specific scopes are enumerated in our{' '}
        <a href="/privacy">Privacy Policy</a> section 4. WAVE never requests or
        stores publishing scopes on any platform.
      </p>
      <p>
        Creators may revoke OAuth access at any time, either inside WAVE
        (&ldquo;Connected Accounts&rdquo;) or in the social platform&rsquo;s app
        settings. Revoking access does not cancel any wave already in progress;
        any committed deliverables remain due.
      </p>

      <h2>8. Acceptable use</h2>
      <p>You agree <strong>not</strong> to:</p>
      <ul>
        <li>Use WAVE to promote illegal goods or services, hate speech, harassment, sexually explicit content, or content that violates a social platform&rsquo;s community guidelines.</li>
        <li>Buy or use fake followers, fake engagement, or any form of artificial amplification.</li>
        <li>Reverse-engineer, scrape, or otherwise replicate the WAVE matching engine, deployment algorithm, or Digital DNA model.</li>
        <li>Circumvent the platform to negotiate or pay outside of WAVE for any wave introduced through the platform (12-month non-circumvent).</li>
        <li>Misrepresent yourself, your audience, or your brand metrics.</li>
      </ul>
      <p>
        We may suspend or terminate accounts that violate these rules, at our sole
        discretion, with as much notice as the situation reasonably allows.
      </p>

      <h2>9. Intellectual property</h2>
      <ul>
        <li>The WAVE platform, software, design, &ldquo;Digital DNA&rdquo; matching engine, and the &ldquo;wave&rdquo; deployment methodology are owned by WAVE DSP, Inc. and protected by copyright, trademark, and patent law (including pending claims).</li>
        <li>Creators retain ownership of their content. Brands retain ownership of their marks.</li>
        <li>By accepting a wave, the creator grants WAVE and the brand a non-exclusive, worldwide, royalty-free licence to display, distribute, and re-share the wave content during the term defined in the brief.</li>
      </ul>

      <h2>10. Termination</h2>
      <p>
        You may close your account at any time from the account settings page.
        WAVE may suspend or terminate accounts for violations of these Terms,
        non-payment, or as required by law. On termination:
      </p>
      <ul>
        <li>Outstanding creator earnings are paid out per the standard schedule.</li>
        <li>Outstanding brand escrow is refunded minus any earned creator payouts.</li>
        <li>Personal data is deleted per the <a href="/privacy">Privacy Policy</a>, retaining only what tax/legal rules require.</li>
      </ul>

      <h2>11. Disclaimers</h2>
      <p>
        WAVE is provided &ldquo;as is&rdquo; without warranty of any kind, express or
        implied. We do not warrant that any wave will achieve specific reach,
        engagement, sales, or ROI outcomes &mdash; performance depends on the brief,
        creators, audience, and the social platforms&rsquo; algorithms. We do our
        best to keep the platform online and accurate, but bugs and downtime can
        and will happen.
      </p>

      <h2>12. Limitation of liability</h2>
      <p>
        To the maximum extent allowed by law, WAVE&rsquo;s total liability for any
        claim arising out of or related to these Terms or the platform is limited
        to the greater of (a) the fees paid to WAVE by you in the 12 months before
        the claim, or (b) USD $500. WAVE is not liable for indirect, incidental,
        consequential, or punitive damages.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to indemnify and hold WAVE harmless from claims arising out of
        your content, your wave activity, or your breach of these Terms &mdash;
        including (for brands) claims related to product liability or false
        advertising, and (for creators) claims related to undisclosed sponsorship
        or copyright infringement in your content.
      </p>

      <h2>14. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware, USA, without
        regard to conflict-of-law principles. Disputes will be resolved by binding
        arbitration in Wilmington, Delaware under the rules of the American
        Arbitration Association, except that either party may seek injunctive relief
        in court for IP-related claims. <strong>You waive any right to participate
        in a class action.</strong>
      </p>

      <h2>15. Changes</h2>
      <p>
        We may update these Terms from time to time. Material changes (fees,
        payouts, prohibited use) will be announced at least 14 days in advance via
        email and in-app notice. Continued use after changes take effect constitutes
        acceptance.
      </p>

      <h2>16. Contact</h2>
      <p>
        <strong>WAVE DSP, Inc.</strong><br />
        Email: <a href="mailto:legal@wavedsp.ai">legal@wavedsp.ai</a><br />
        Privacy: <a href="mailto:privacy@wavedsp.ai">privacy@wavedsp.ai</a><br />
        Security: <a href="mailto:security@wavedsp.ai">security@wavedsp.ai</a>
      </p>
    </LegalLayout>
  );
}
